'use client'

import { Radio } from 'fukku/Radio'
import { type JSX, useMemo } from 'react'
import type { RefundData, RefundDataKey } from 'refunds/types/refundData'

import { AccountBalanceOption } from './content/AccountBalanceOption'
import { BankOption } from './content/BankOption'
import { MoneyCashOption } from './content/MoneyCashOption'

import style from './RefundSelectorContainer.module.scss'

interface RefundSelectorContainerProps {
	readonly refundData: RefundData
	readonly isUniqueMethod: boolean
	readonly onChange?: (updatedRefundData: RefundData) => void
}

export function RefundSelectorContainer({
	refundData,
	isUniqueMethod,
	onChange,
}: RefundSelectorContainerProps) {
	const { accountBalance, bankData, moneyCash } = refundData
	const { selected: isBankDataSelected, available: bankDataAvailable } =
		bankData ?? {}

	const { selected: isAccountBalanceSelected, balanceFormatted } =
		accountBalance ?? {}

	const { selected: isMoneyCashSelected } = moneyCash ?? {}

	const defaultSelectedOption = useMemo(() => {
		return (
			(Object.keys(refundData) as RefundDataKey[]).find(
				(key) => refundData[key]?.selected
			) ?? null
		)
	}, [refundData])

	const refundOptions = useMemo(() => {
		const options: { value: RefundDataKey; content: JSX.Element }[] = []

		if (accountBalance) {
			options.push({
				value: 'accountBalance',
				content: (
					<AccountBalanceOption
						selected={isAccountBalanceSelected || isUniqueMethod}
						balanceFormatted={balanceFormatted ?? ''}
					/>
				),
			})
		}

		if (bankDataAvailable) {
			options.push({
				value: 'bankData',
				content: <BankOption selected={!!isBankDataSelected} />,
			})
		}

		if (moneyCash) {
			options.push({
				value: 'moneyCash',
				content: <MoneyCashOption selected={!!isMoneyCashSelected} />,
			})
		}

		return options
	}, [refundData, isUniqueMethod])

	if (!refundData || !refundOptions.length) {
		return null
	}

	if (refundOptions.length === 1) {
		return refundOptions[0].content
	}

	const handleOnChange = async (selectedOption: RefundDataKey) => {
		const updatedRefundData = { ...refundData }
		;(Object.keys(updatedRefundData) as RefundDataKey[]).forEach((key) => {
			const data = updatedRefundData[key]
			if (data) {
				data.selected = key === selectedOption
			}
		})

		if (onChange) {
			return onChange(updatedRefundData)
		}

		return updatedRefundData
	}

	return (
		<Radio
			id='refund-options'
			name='refund-options'
			className={`${style.options} ${style.fullwidth}`}
			options={refundOptions}
			onChange={(value) => handleOnChange(value.toString() as RefundDataKey)}
			{...(defaultSelectedOption && { defaultSelected: defaultSelectedOption })}
			legend=''
			data-testid='refund-options'
		/>
	)
}
